import { createApi } from '@reduxjs/toolkit/query/react';
import { SALES_API_URL } from '../settings/config';
import { authRequest } from '../store/authorizedRequests';
import {
    BillingInfo,
    CancelSubscriptionParams,
    CreateSubscriptionParams,
    FetchInvoicesResponse,
    FetchPdfInvoiceParams,
    FetchPdfInvoiceResponse,
    SubscriptionPlans,
    ThreeDSecureResponse,
    UpdateBillingInfoParams,
    Subscription,
    UpdatePaymentMethodParams,
    UpdateSubscriptionParams,
    SubscriptionResponse,
    ErrorResponse,
} from './types';

const salesBaseUrl = `${SALES_API_URL}`;
const SALES_REFETCH_TIME = 28800;

export const salesApi = createApi({
    reducerPath: 'api.sales',
    keepUnusedDataFor: SALES_REFETCH_TIME,
    tagTypes: ['BillingInfo', 'Subscription'],

    baseQuery: async (args, api, extraOptions) => {
        const { url, method, body, signal } = args;
        const { dispatch, getState } = api;
        try {
            const data = await authRequest(salesBaseUrl.concat(url), dispatch, getState, {
                method: method,
                body: JSON.stringify(body),
                signal: signal,
            });

            if (typeof data === 'undefined') {
                return { data: null };
            }

            return { data };
        } catch (error) {
            return { error };
        }
    },

    endpoints: (builder) => ({
        fetchSubscriptionPlans: builder.query<SubscriptionPlans, void>({
            query: () => {
                return {
                    method: 'GET',
                    url: '/plans',
                };
            },
        }),

        fetchSubscription: builder.query<Subscription, void>({
            query: () => {
                return {
                    method: 'GET',
                    url: '/subscription',
                };
            },

            providesTags: ['Subscription'],
        }),

        createSubscription: builder.mutation<ErrorResponse | ThreeDSecureResponse | SubscriptionResponse, CreateSubscriptionParams>({
            query: (args) => {
                return {
                    method: 'POST',
                    url: '/subscription',
                    body: args,
                };
            },

            invalidatesTags: (response, error, args) => (response && 'status' in response ? ['Subscription', 'BillingInfo'] : []),
        }),

        updateSubscription: builder.mutation<ErrorResponse | SubscriptionResponse, UpdateSubscriptionParams>({
            query: (args) => {
                return {
                    method: 'PATCH',
                    url: '/subscription',
                    body: args,
                };
            },

            invalidatesTags: (response, error, args) => (response && 'status' in response ? ['Subscription'] : []),
        }),

        cancelSubscription: builder.mutation<SubscriptionResponse, CancelSubscriptionParams>({
            query: (args) => {
                return {
                    method: 'DELETE',
                    url: '/subscription',
                    body: args,
                };
            },

            invalidatesTags: ['Subscription', 'BillingInfo'],
        }),

        addCancellationReason: builder.mutation<void, CancelSubscriptionParams>({
            query: (args) => {
                return {
                    method: 'POST',
                    url: '/subscription/reason',
                    body: args,
                };
            },
        }),

        fetchBillingInfo: builder.query<BillingInfo, void>({
            query: () => {
                return {
                    method: 'GET',
                    url: '/billing_info',
                };
            },

            providesTags: ['BillingInfo'],
        }),

        updateBillingInfo: builder.mutation<BillingInfo, UpdateBillingInfoParams>({
            query: (args) => {
                return {
                    method: 'POST',
                    url: '/billing_info',
                    body: args,
                };
            },

            invalidatesTags: ['BillingInfo'],
        }),

        updatePaymentMethod: builder.mutation<ErrorResponse | ThreeDSecureResponse | BillingInfo, UpdatePaymentMethodParams>({
            query: (args) => {
                return {
                    method: 'POST', //TODO: change to patch (from old operations.js)
                    url: '/billing_info/payment_method',
                    body: args,
                };
            },

            invalidatesTags: (response, error, args) => (response && 'card_type' in response ? ['BillingInfo'] : []),
        }),

        fetchInvoices: builder.query<FetchInvoicesResponse, void>({
            query: () => {
                return {
                    method: 'GET',
                    url: '/invoices',
                };
            },
        }),

        fetchPdfInvoice: builder.query<FetchPdfInvoiceResponse, FetchPdfInvoiceParams>({
            query: (args) => {
                const { invoiceNumber } = args;

                return {
                    method: 'GET',
                    url: `/invoices/pdf/${invoiceNumber}`,
                };
            },
        }),
    }),
});

export const {
    useFetchSubscriptionPlansQuery,
    useFetchSubscriptionQuery,
    useCancelSubscriptionMutation,
    useAddCancellationReasonMutation,
    useFetchBillingInfoQuery,
    useUpdateBillingInfoMutation,
    useUpdatePaymentMethodMutation,
    useFetchInvoicesQuery,
    useLazyFetchPdfInvoiceQuery,
    useCreateSubscriptionMutation,
    useUpdateSubscriptionMutation,
} = salesApi;
